import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Plus } from "lucide-react";
import CustomMetricsDialog from "./CustomMetricsDialog";
import { getLocalizedMetricName } from "@/lib/utils";

interface AddMetricDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  allMetrics: Array<{ key: string; name: string; unit: string; emoji?: string }>;
  metricPreferences: any;
  customMetrics: Array<{
    metric_key: string;
    metric_name: string;
    metric_emoji: string;
    metric_unit: string;
    metric_type?: string;
    is_inverse?: boolean
  }>;
  displayedMetricKeys: Set<string>; // Keys of metrics already shown in the Update Today's Totals dialog
  manuallyAddedMetrics: Array<{ key: string; name: string; existingMetricKey?: string }>; // Metrics that have been manually added but not yet saved
  onAddBuiltInMetric: (metricKey: string) => void;
  onAddExistingCustomMetric: (metric: any) => void;
  onCreateCustomMetric: (newMetric: {
    key: string;
    name: string;
    emoji: string;
    unit: string;
    type: "numeric" | "boolean";
    goal: number;
    value: number;
    inverse: boolean;
    isAdditive?: boolean;
    isRelative?: boolean;
  }) => void;
  hideColorPicker?: boolean; // When true, hides color picker in CustomMetricsDialog (for anonymous/onboarding)
}

export const AddMetricDialog = ({
  open,
  onOpenChange,
  userId,
  allMetrics,
  metricPreferences,
  customMetrics,
  displayedMetricKeys,
  manuallyAddedMetrics,
  onAddBuiltInMetric,
  onAddExistingCustomMetric,
  onCreateCustomMetric,
  hideColorPicker = false,
}: AddMetricDialogProps) => {
  const { t } = useTranslation();
  const [customMetricsDialogOpen, setCustomMetricsDialogOpen] = useState(false);

  // Get all built-in metrics that are NOT currently displayed
  const availableBuiltInMetrics = allMetrics.filter(metric => {
    // Skip if already displayed
    if (displayedMetricKeys.has(metric.key)) return false;

    // Check if it's a built-in metric (not a custom metric)
    if (metric.key.startsWith('custom_')) return false;

    // Filter out HealthKit metrics
    if (metric.key.startsWith('healthkit_')) return false;

    return true;
  });

  // Get all existing custom metrics that are NOT currently displayed
  const availableCustomMetrics = customMetrics.filter(metric => {
    const metricValueKey = `${metric.metric_key}_value`;

    // Check if already displayed
    if (displayedMetricKeys.has(metricValueKey) || displayedMetricKeys.has(metric.metric_key)) {
      return false;
    }

    // Check if already manually added (including newly created ones)
    const alreadyManuallyAdded = manuallyAddedMetrics.some(m => {
      // Check if it's an existing metric that was manually added
      return m.existingMetricKey === metric.metric_key;
    });

    if (alreadyManuallyAdded) {
      return false;
    }

    return true;
  });

  const handleAddBuiltInMetric = (metricKey: string) => {
    onAddBuiltInMetric(metricKey);
    onOpenChange(false);
  };

  const handleAddExistingCustomMetric = (metric: any) => {
    onAddExistingCustomMetric(metric);
    onOpenChange(false);
  };

  const handleOpenCustomMetricsDialog = () => {
    setCustomMetricsDialogOpen(true);
  };

  const handleCreateCustomMetric = (newMetric: any) => {
    onCreateCustomMetric(newMetric);
    setCustomMetricsDialogOpen(false);
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md max-h-[85vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              {t('components.add_metric_dialog.title')}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 overflow-y-auto flex-1 min-h-0 py-2">
            {/* Create Your Own - Top Section */}
            <div className="space-y-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleOpenCustomMetricsDialog}
                className="w-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('components.add_metric_dialog.create_own')}
              </Button>
            </div>

            {/* Divider with text */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-border" />
              <span className="text-xs text-muted-foreground">{t('components.add_metric_dialog.pick_popular')}</span>
              <div className="flex-1 h-px bg-border" />
            </div>

            {/* Built-in Metrics Section */}
            {availableBuiltInMetrics.length > 0 && (
              <div className="space-y-2">
                <div className="space-y-1">
                  {availableBuiltInMetrics.map(metric => (
                    <button
                      key={metric.key}
                      onClick={() => handleAddBuiltInMetric(metric.key)}
                      className="w-full flex items-center justify-between p-2 rounded-md hover:bg-muted/50 transition-colors text-left"
                    >
                      <div className="flex items-center gap-2">
                        {metric.emoji && <span className="text-lg">{metric.emoji}</span>}
                        <div>
                          <div className="text-sm font-medium">{getLocalizedMetricName(metric.key, t, metric.name)}</div>
                          <div className="text-xs text-muted-foreground">{metric.unit}</div>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7 px-3 text-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddBuiltInMetric(metric.key);
                        }}
                      >
                        {t('components.add_metric_dialog.add_btn')}
                      </Button>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Existing Custom Metrics Section */}
            {availableCustomMetrics.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-muted-foreground px-1">{t('components.add_metric_dialog.your_custom')}</h3>
                <div className="space-y-1">
                  {availableCustomMetrics.map(metric => {
                    const isBoolean = (metric as any).metric_type === "boolean";
                    return (
                      <button
                        key={metric.metric_key}
                        onClick={() => handleAddExistingCustomMetric(metric)}
                        className="w-full flex items-center justify-between p-2 rounded-md hover:bg-muted/50 transition-colors text-left"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{metric.metric_emoji}</span>
                          <div>
                            <div className="text-sm font-medium">{metric.metric_name}</div>
                            <div className="text-xs text-muted-foreground">
                              {isBoolean ? t('components.custom_metrics_dialog.type_yes_no') : metric.metric_unit}
                            </div>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-7 px-3 text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddExistingCustomMetric(metric);
                          }}
                        >
                          {t('components.add_metric_dialog.add_btn')}
                        </Button>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Empty State */}
            {availableBuiltInMetrics.length === 0 && availableCustomMetrics.length === 0 && (
              <div className="text-center py-6 text-sm text-muted-foreground">
                {t('components.add_metric_dialog.all_added')}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Custom Metrics Dialog for creating new metrics */}
      <CustomMetricsDialog
        userId={userId}
        open={customMetricsDialogOpen}
        onOpenChange={setCustomMetricsDialogOpen}
        onCreate={handleCreateCustomMetric}
        hideColorPicker={hideColorPicker}
        existingCustomMetrics={[
          // Include saved custom metrics
          ...customMetrics.map(m => ({
            metric_key: m.metric_key,
            metric_name: m.metric_name,
            metric_emoji: m.metric_emoji,
            metric_type: m.metric_type,
          })),
          // Include manually added metrics (temporary metrics from Update Today's Totals dialog)
          ...manuallyAddedMetrics.map(m => ({
            metric_key: m.key,
            metric_name: m.name,
            metric_emoji: (m as any).emoji || "⭐",
            metric_type: (m as any).type || "numeric",
          })),
        ]}
      />
    </>
  );
};
